import React from 'react';

export const TranscriptionSkeletonHeader: React.FC = () => (
  <div className="flex items-center justify-between py-1 px-1 mb-1">
    <div className="flex items-center gap-4">
      <div className="h-9 w-9 bg-slate-200 dark:bg-zinc-700 rounded-lg animate-pulse" />
      <div className="space-y-2">
        <div className="h-5 w-32 bg-slate-200 dark:bg-zinc-700 rounded animate-pulse" />
        <div className="h-4 w-48 bg-slate-100 dark:bg-zinc-800 rounded animate-pulse" />
      </div>
    </div>
    <div className="flex items-center gap-2">
      <div className="h-9 w-9 bg-slate-100 dark:bg-zinc-800 rounded animate-pulse" />
      <div className="h-9 w-36 bg-indigo-200 dark:bg-indigo-900/50 rounded animate-pulse" />
    </div>
  </div>
);

interface TranscriptionSkeletonBodyProps {
  sidebarWidth?: number;
}

export const TranscriptionSkeletonBody: React.FC<TranscriptionSkeletonBodyProps> = ({ sidebarWidth = 60 }) => {
  return (
    <div className="flex-1 flex flex-col lg:flex-row gap-6 min-h-0">
      {/* Left Column - Transcript */}
      <div 
        className="w-full lg:flex-none flex flex-col bg-white dark:bg-zinc-900 rounded-lg border border-slate-200 dark:border-zinc-700 overflow-hidden"
        style={{ flex: `0 0 ${sidebarWidth}%` }}
      >
        {/* Toolbar */}
        <div className="flex items-center justify-between px-4 py-1.5 border-b border-slate-200 dark:border-zinc-700">
          <div className="h-5 w-20 bg-slate-200 dark:bg-zinc-700 rounded animate-pulse" />
          <div className="flex gap-2">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-8 w-8 bg-slate-100 dark:bg-zinc-800 rounded animate-pulse" />
            ))}
          </div>
        </div>
        {/* Rating */}
        <div className="flex items-center justify-between px-4 py-3 bg-slate-50 dark:bg-zinc-800/50 border-b border-slate-200 dark:border-zinc-700">
          <div className="h-5 w-48 bg-slate-200 dark:bg-zinc-700 rounded animate-pulse" />
        </div>
        {/* Segments */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="flex gap-3">
              <div className="h-7 w-7 bg-indigo-100 dark:bg-indigo-900/30 rounded-full animate-pulse" />
              <div className="flex-1 space-y-2">
                <div className="h-4 w-24 bg-slate-200 dark:bg-zinc-700 rounded animate-pulse" />
                <div className="h-4 w-full bg-slate-100 dark:bg-zinc-800 rounded animate-pulse" />
                <div className="h-4 w-3/4 bg-slate-100 dark:bg-zinc-800 rounded animate-pulse" />
              </div>
            </div>
          ))}
        </div>
        {/* Player */}
        <div className="h-16 bg-slate-50 dark:bg-zinc-800 border-t border-slate-200 dark:border-zinc-700 animate-pulse" />
      </div>

      {/* Right Column - AI Insights */}
      <div className="w-full lg:flex-1 bg-white dark:bg-zinc-900 rounded-lg border border-slate-200 dark:border-zinc-700 overflow-hidden min-h-[300px] lg:min-h-0">
        <div className="flex border-b border-slate-200 dark:border-zinc-700">
          <div className="flex-1 py-3 flex justify-center">
            <div className="h-5 w-20 bg-slate-200 dark:bg-zinc-700 rounded animate-pulse" />
          </div>
          <div className="flex-1 py-3 flex justify-center">
            <div className="h-5 w-20 bg-slate-100 dark:bg-zinc-800 rounded animate-pulse" />
          </div>
        </div>
        <div className="p-6 space-y-8">
          <div className="space-y-4">
            <div className="h-5 w-20 bg-slate-200 dark:bg-zinc-700 rounded animate-pulse" />
            <div className="flex flex-col items-center py-8">
              <div className="h-16 w-16 bg-slate-100 dark:bg-zinc-800 rounded-2xl animate-pulse mb-4" />
              <div className="h-4 w-48 bg-slate-100 dark:bg-zinc-800 rounded animate-pulse mb-2" />
              <div className="h-4 w-36 bg-slate-100 dark:bg-zinc-800 rounded animate-pulse mb-4" />
              <div className="h-9 w-24 bg-indigo-200 dark:bg-indigo-900/50 rounded animate-pulse" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
